import { z } from 'zod';

export const SECRET_KEYS = ['openai', 'anthropic', 'supadata', 'usda'] as const;
export type SecretKey = (typeof SECRET_KEYS)[number];
export const llmProviderSchema = z.enum(['openai', 'anthropic']);

const secretStatusSchema = z.object({
  set: z.boolean(),
  /** Where the effective key comes from. */
  source: z.enum(['settings', 'env']).nullable(),
  /** Last four characters, to recognise the key; the key itself is never returned. */
  last4: z.string().nullable(),
});

/** Settings → AI as the admin sees it (secrets write-only). */
export const aiSettingsSchema = z.object({
  provider: llmProviderSchema,
  modelOpenai: z.string().nullable(),
  modelAnthropic: z.string().nullable(),
  defaultModels: z.object({ openai: z.string(), anthropic: z.string() }),
  monthlyBudgetUsd: z.number().nullable(),
  spentThisMonthUsd: z.number(),
  keys: z.object({
    openai: secretStatusSchema,
    anthropic: secretStatusSchema,
    supadata: secretStatusSchema,
    usda: secretStatusSchema,
  }),
  /** SETTINGS_ENCRYPTION_KEY is set, so keys can be saved. */
  canStoreKeys: z.boolean(),
});
export type AiSettings = z.infer<typeof aiSettingsSchema>;

const secretInput = z.string().trim().min(8).max(500).nullable();

export const aiSettingsUpdateSchema = z
  .object({
    provider: llmProviderSchema,
    modelOpenai: z.string().trim().min(1).max(100).nullable(),
    modelAnthropic: z.string().trim().min(1).max(100).nullable(),
    monthlyBudgetUsd: z.number().nonnegative().max(10_000).nullable(),
    /** A new key, or null to remove the stored one. Omitted keys stay as they are. */
    keys: z
      .object({
        openai: secretInput,
        anthropic: secretInput,
        supadata: secretInput,
        usda: secretInput,
      })
      .partial(),
  })
  .partial();
export type AiSettingsUpdate = z.infer<typeof aiSettingsUpdateSchema>;

export const connectionTestSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), provider: z.string(), model: z.string(), latencyMs: z.number() }),
  z.object({ ok: z.literal(false), kind: z.string(), message: z.string() }),
]);
export type ConnectionTest = z.infer<typeof connectionTestSchema>;
